import React from 'react';
import { Section, SectionHeading, Reveal } from './ui/Section';

/**
 * 料金の数値は経営判断に直結するため、勝手に変更しないこと。
 * 変更が必要な場合は必ず依頼元に確認する。
 */
const PLAN = {
  name: 'スタンダードプラン',
  description: '中小企業に最適な全機能プラン',
  monthlyEquivalent: '492',
  annual: '5,900',
  monthly: '980',
  saving: '5,880',
  features: [
    '無制限の仕訳入力',
    'AI自動仕訳機能',
    'AI-OCR無制限利用',
    '決算書自動作成',
    '経営分析レポート',
    '複数ユーザー対応（5名まで）',
    'データバックアップ',
    'メールサポート',
  ],
};

const Check: React.FC = () => (
  <svg
    viewBox="0 0 20 20"
    className="mt-[7px] h-4 w-4 shrink-0 text-brand-500"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m3 10 5 5 9-11" />
  </svg>
);

const Pricing: React.FC = () => (
  <Section id="pricing" tone="white">
    <SectionHeading
      eyebrow="Pricing"
      title="料金プラン"
      lead="シンプルでお得な料金体系。プランは1つだけ、機能制限もありません。"
    />

    <Reveal delay={0.1}>
      <div className="mx-auto mt-14 max-w-4xl overflow-hidden rounded-card border-2 border-ink-900 bg-white shadow-card">
        {/* プラン名 */}
        <div className="flex flex-col items-center gap-1 bg-ink-900 px-8 py-5 text-center">
          <h3 className="text-lg font-bold text-white">{PLAN.name}</h3>
          <p className="text-small text-ink-200">{PLAN.description}</p>
        </div>

        <div className="grid md:grid-cols-2">
          {/* 左：価格 */}
          <div className="border-b border-line px-8 py-10 text-center md:border-b-0 md:border-r">
            <p className="text-small text-body-soft">月あたりわずか</p>
            <p className="mt-2 flex items-baseline justify-center gap-1">
              <span className="num text-6xl font-bold leading-none text-ink-900">
                {PLAN.monthlyEquivalent}
              </span>
              <span className="text-2xl font-bold text-ink-900">円</span>
            </p>
            <p className="mt-3 text-small text-body-faint">
              コーヒー1杯分で、プロの会計システムが使い放題
            </p>

            <div className="mt-7 rounded-card bg-surface-muted px-5 py-4">
              <p className="font-bold text-ink-900">
                一括支払 <span className="num">{PLAN.annual}</span>円/年
              </p>
              <div className="mt-3 border-t border-line pt-3 text-small text-body-soft">
                <p>
                  通常月払いなら <span className="num">{PLAN.monthly}</span>円/月
                </p>
                <p className="mt-0.5 font-bold text-brand-600">
                  （年払いで<span className="num">{PLAN.saving}</span>円お得）
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <span className="rounded-pill bg-brand-50 px-4 py-1.5 text-small font-bold text-brand-700">
                初期費用0円
              </span>
              <span className="rounded-pill bg-brand-50 px-4 py-1.5 text-small font-bold text-brand-700">
                30日間無料体験
              </span>
            </div>
          </div>

          {/* 右：機能 */}
          <div className="px-8 py-10">
            <p className="text-eyebrow uppercase num text-body-faint">Included</p>
            <p className="mt-2 font-bold text-ink-900">すべての機能が含まれます</p>
            <ul className="mt-6 space-y-3">
              {PLAN.features.map((f) => (
                <li key={f} className="flex gap-3 text-small text-body-DEFAULT">
                  <Check />
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="border-t border-line bg-surface-muted px-8 py-7 text-center">
          <a href="/monitor.html" className="btn btn-lg btn-primary w-full sm:w-auto sm:px-16">
            30日間無料で始める
          </a>
          <p className="mt-3 text-small text-body-faint">
            クレジットカード不要で、すぐに始められます
          </p>
        </div>
      </div>
    </Reveal>

    {/* 低価格の理由 */}
    <Reveal delay={0.2}>
      <div className="mx-auto mt-10 max-w-3xl rounded-card border border-line bg-surface-muted p-8 text-center">
        <h4 className="text-h3 text-ink-900">なぜ、この価格で提供できるのか</h4>
        <p className="mt-4 text-body leading-relaxed text-body-soft">
          外部接続一切不要、すべてのデータはお客様のPC内で完結。
          クラウドへの依存ゼロで、データは100%社内保存、情報漏洩リスクゼロ。
          高額なクラウド費用が不要なため、月額費用を大幅削減しています。
        </p>
      </div>
    </Reveal>
  </Section>
);

export default Pricing;
